import { useEffect, useRef, useState } from 'react';

/**
 * Horizontal scroll area with fade gradients on left/right edges.
 * Gradients appear only when content overflows AND there is more to scroll in that direction.
 *
 * Props:
 *  - children: the scrollable row / table
 *  - className: extra classes for the inner scroll container
 *  - fadeColor: Tailwind gradient color class (default slate-900)
 *  - layout: 'flex' (default) for nav rows | 'block' for tables / arbitrary content
 *  - innerProps: extra props/testid for the inner scroll container
 */
export const HorizontalScroller = ({
  children,
  className = '',
  fadeColor = 'from-slate-900',
  layout = 'flex',
  innerProps = {},
}) => {
  const ref = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const update = () => {
    const el = ref.current;
    if (!el) return;
    const canScroll = el.scrollWidth > el.clientWidth + 1;
    setShowLeft(canScroll && el.scrollLeft > 2);
    setShowRight(canScroll && el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // ResizeObserver catches when children change or nav bar changes size
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="relative">
      <div
        ref={ref}
        {...innerProps}
        className={`${layout === 'flex' ? 'flex snap-x' : 'block'} overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden [scrollbar-width:none] touch-pan-x ${className}`}
        style={{ WebkitOverflowScrolling: 'touch', ...(innerProps.style || {}) }}
      >
        {children}
      </div>
      {/* Left fade */}
      <div
        className={`pointer-events-none absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r ${fadeColor} to-transparent transition-opacity duration-200 ${
          showLeft ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
      />
      {/* Right fade */}
      <div
        className={`pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l ${fadeColor} to-transparent transition-opacity duration-200 ${
          showRight ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
      />
    </div>
  );
};

export default HorizontalScroller;
